import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Select from "react-select"
import DatePicker from "react-datepicker"
import moment from "moment"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"

const service = new UserService()

const GROUP_BY_OPTIONS = [
  "Contact Leader",
  "Manager",
  "Senior Manager",
  "Regional",
  "Senior Regional",
  "Partner",
  "Division",
]
const GROUP_BY_TO_API: Record<string, string> = {
  "Contact Leader": "Contact Leader",
  Manager: "Manager",
  "Senior Manager": "Senior Manager",
  Regional: "Regional",
  "Senior Regional": "Senior Regional",
  Partner: "Partner",
  Division: "Division",
}

const STAGE_COLUMNS = [
  { key: "contact", label: "Contact" },
  { key: "contact_plus", label: "Contact+" },
  { key: "signed", label: "Signed" },
  { key: "signed_plus", label: "Signed+" },
  { key: "recruiter", label: "Recruiter" },
  { key: "recruiter_plus", label: "Recruiter+" },
  { key: "sh_pro", label: "SH Pro" },
  { key: "sh_pro_plus", label: "SH Pro+" },
]

type ContactsRow = {
  _id: string
  name: string
  count: number
  contact?: number
  contact_plus?: number
  signed?: number
  signed_plus?: number
  recruiter?: number
  recruiter_plus?: number
  sh_pro?: number
  sh_pro_plus?: number
}

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
}: {
  value: number
  maxVal: number
  colorClass?: string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  return (
    <div className="flex items-center gap-2 min-w-[70px]">
      <span className="text-sm tabular-nums shrink-0 text-ink">{value.toLocaleString()}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[40px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

const BAR_COLORS: Record<string, string> = {
  contact: "bg-sky-500",
  contact_plus: "bg-cyan-500",
  signed: "bg-emerald-500",
  signed_plus: "bg-teal-500",
  recruiter: "bg-amber-500",
  recruiter_plus: "bg-orange-500",
  sh_pro: "bg-violet-500",
  sh_pro_plus: "bg-purple-500",
}

export const ContactsReport = () => {
  const [date, setDate] = useState<string>("YEARTODATE")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [groupBy, setGroupBy] = useState<string>("Contact Leader")
  const [source, setSource] = useState<string>("")
  const [location, setLocation] = useState<string>("")
  const [contactLeaderId, setContactLeaderId] = useState<string>("")
  const [recruiterId, setRecruiterId] = useState<string>("")
  const [maxPosition, setMaxPosition] = useState<string>("")
  const [tenure, setTenure] = useState<string>("")

  const [filterOptions, setFilterOptions] = useState<{
    sources: string[]
    locations: string[]
    maxPositions: string[]
    tenures: string[]
  }>({ sources: [], locations: [], maxPositions: [], tenures: [] })
  const [contactLeadersFromServer, setContactLeadersFromServer] = useState<{ badgeid: string; name: string }[]>([])
  const [recruitersFromServer, setRecruitersFromServer] = useState<{ badgeid: string; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [apiData, setApiData] = useState<Record<string, ContactsRow[]> | null>(null)

  const groupByApi = groupBy ? (GROUP_BY_TO_API[groupBy] ?? "") : ""

  const contactLeaderOptions = useMemo(
    () => [
      { value: "", label: "All" },
      ...contactLeadersFromServer.map((u) => ({
        value: u.badgeid,
        label: u.name ? `${u.name} (${u.badgeid})` : u.badgeid,
      })),
    ],
    [contactLeadersFromServer]
  )
  const recruiterOptions = useMemo(
    () => [
      { value: "", label: "All" },
      ...recruitersFromServer.map((u) => ({
        value: u.badgeid,
        label: u.name ? `${u.name} (${u.badgeid})` : u.badgeid,
      })),
    ],
    [recruitersFromServer]
  )
  const sourceOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.sources.map((s) => ({ value: s, label: s }))],
    [filterOptions.sources]
  )
  const locationOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.locations.map((s) => ({ value: s, label: s }))],
    [filterOptions.locations]
  )
  const maxPositionOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.maxPositions.map((s) => ({ value: s, label: s }))],
    [filterOptions.maxPositions]
  )
  const tenureOptions = useMemo(
    () => [{ value: "", label: "All" }, ...filterOptions.tenures.map((s) => ({ value: s, label: s }))],
    [filterOptions.tenures]
  )

  const queryParams = useMemo(
    () => ({
      date: date || undefined,
      groupBy: groupByApi || undefined,
      source: source.trim() || undefined,
      location: location.trim() || undefined,
      contactLeaderId: contactLeaderId || undefined,
      recruiterId: recruiterId || undefined,
      maxPosition: maxPosition || undefined,
      tenure: tenure || undefined,
    }),
    [date, groupByApi, source, location, contactLeaderId, recruiterId, maxPosition, tenure]
  )

  const fetchReport = useCallback(() => {
    if (!groupByApi) return
    setLoading(true)
    service
      .getContactsRegionalReporting(queryParams)
      .then((resp: any) => {
        if (resp?.data?.success && resp?.data?.data) setApiData(resp.data.data)
        else setApiData(null)
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false))
  }, [queryParams, groupByApi])

  const fetchContactLeaders = useCallback(() => {
    service.getFilters("Contact Leader").then((resp: any) => {
      const list = resp?.data?.data ?? resp?.data ?? []
      setContactLeadersFromServer(Array.isArray(list) ? list : [])
    })
  }, [])
  const fetchRecruiters = useCallback(() => {
    service.getFilters("Recruiter").then((resp: any) => {
      const list = resp?.data?.data ?? resp?.data ?? []
      setRecruitersFromServer(Array.isArray(list) ? list : [])
    })
  }, [])
  const fetchFilterOptions = useCallback(() => {
    service.getContactsFilterOptions().then((resp: any) => {
      const d = resp?.data?.data ?? resp?.data ?? {}
      setFilterOptions({
        sources: Array.isArray(d.sources) ? d.sources : [],
        locations: Array.isArray(d.locations) ? d.locations : [],
        maxPositions: Array.isArray(d.maxPositions) ? d.maxPositions : [],
        tenures: Array.isArray(d.tenures) ? d.tenures : [],
      })
    })
  }, [])

  const initialFetchDone = useRef(false)
  useEffect(() => {
    if (!initialFetchDone.current && groupByApi) {
      initialFetchDone.current = true
      fetchReport()
    }
  }, [groupByApi, fetchReport])

  useEffect(() => {
    fetchContactLeaders()
    fetchRecruiters()
    fetchFilterOptions()
  }, [fetchContactLeaders, fetchRecruiters, fetchFilterOptions])

  const { tableRows, dataGroupBy } = useMemo(() => {
    if (!apiData) return { tableRows: [] as ContactsRow[], dataGroupBy: null }
    const keyForCurrent = groupByApi ? `by_${groupByApi}` : null
    const arr = keyForCurrent ? apiData[keyForCurrent] : undefined
    if (Array.isArray(arr) && arr.length > 0) {
      return { tableRows: arr, dataGroupBy: groupByApi }
    }
    const firstKey = Object.keys(apiData)[0]
    const fallback = firstKey && Array.isArray(apiData[firstKey]) ? apiData[firstKey] : []
    const fallbackGroupBy = firstKey ? firstKey.replace(/^by_/, "") : null
    return { tableRows: fallback, dataGroupBy: fallbackGroupBy }
  }, [apiData, groupByApi])

  const maxCount = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.count), 1) : 1), [tableRows])
  const maxByStage = useMemo(() => {
    const m: Record<string, number> = {}
    STAGE_COLUMNS.forEach(({ key }) => {
      m[key] = tableRows.length ? Math.max(...tableRows.map((r) => (r as any)[key] ?? 0), 1) : 1
    })
    return m
  }, [tableRows])

  const totals = useMemo(
    () => ({
      count: tableRows.reduce((s, r) => s + (r.count ?? 0), 0),
      ...STAGE_COLUMNS.reduce(
        (acc, { key }) => ({
          ...acc,
          [key]: tableRows.reduce((s, r) => s + ((r as any)[key] ?? 0), 0),
        }),
        {} as Record<string, number>
      ),
    }),
    [tableRows]
  )

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const colCount = 2 + STAGE_COLUMNS.length

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Contacts Report</h1>
        <p className="page-subtitle">Contact counts by date range and group, with stage breakdown.</p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Date range</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") setDate(e.target.value)
                else setShowCustom(true)
              }}
              className="px-3 py-2 text-sm"
            >
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-56 rounded-lg border border-line bg-white text-ink px-3 text-sm"
                customInput={<input placeholder="Select date" className="rounded-lg border border-line bg-white text-ink w-56 py-2 px-3 text-sm" />}
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label">Source</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={sourceOptions.find((o) => o.value === source) ?? null}
              options={sourceOptions}
              onChange={(opt) => setSource(opt?.value ?? "")}
              className="react-select min-w-[160px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label">Location</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={locationOptions.find((o) => o.value === location) ?? null}
              options={locationOptions}
              onChange={(opt) => setLocation(opt?.value ?? "")}
              className="react-select min-w-[160px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Contact leader</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={contactLeaderOptions.find((o) => o.value === contactLeaderId) ?? null}
              options={contactLeaderOptions}
              onChange={(opt) => setContactLeaderId(opt?.value ?? "")}
              onMenuOpen={fetchContactLeaders}
              className="react-select min-w-[180px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Recruiter</label>
            <Select
              isClearable
              isSearchable
              placeholder="All..."
              value={recruiterOptions.find((o) => o.value === recruiterId) ?? null}
              options={recruiterOptions}
              onChange={(opt) => setRecruiterId(opt?.value ?? "")}
              onMenuOpen={fetchRecruiters}
              className="react-select min-w-[180px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Max position</label>
            <select
              value={maxPosition}
              onChange={(e) => setMaxPosition(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {maxPositionOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Tenure</label>
            <select
              value={tenure}
              onChange={(e) => setTenure(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {tenureOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!groupByApi || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {dataGroupBy && groupByApi && dataGroupBy !== groupByApi && tableRows.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-amber-800 text-sm">
          Showing data for <strong>{dataGroupBy}</strong>. Click Search to load data for <strong>{groupByApi}</strong>.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Contacts by {groupBy || "Group"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Total</th>
                {STAGE_COLUMNS.map(({ key, label }) => (
                  <th key={key} className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !apiData ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">
                    {apiData ? "No data for the selected filters." : "Select group by and click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr
                    key={row._id != null ? String(row._id) : `row-${idx}`}
                    className="border-b border-line hover:bg-surface transition-colors"
                  >
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">{row.name ?? row._id ?? "—"}</td>
                    <td className="px-2 py-3">
                      <BarCell value={row.count} maxVal={maxCount} colorClass="bg-blue-500" />
                    </td>
                    {STAGE_COLUMNS.map(({ key }) => (
                      <td key={key} className="px-2 py-3">
                        <BarCell
                          value={(row as any)[key] ?? 0}
                          maxVal={maxByStage[key]}
                          colorClass={BAR_COLORS[key] ?? "bg-gray-500"}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                <td className="px-2 py-3 whitespace-nowrap">Total</td>
                <td className="px-2 py-3">{totals.count.toLocaleString()}</td>
                {STAGE_COLUMNS.map(({ key }) => (
                  <td key={key} className="px-2 py-3">
                    {(totals as any)[key]?.toLocaleString() ?? "0"}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
